import type { ReactNode } from 'react';
import { integrations, type LoxoneRozpocet } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import { Badge, Button, Card, PageHeader } from '../components/ui.js';
import { IconPlug, IconRefresh } from '../components/icons.js';
import { Data, POPIS_INTEGRACIE, TON_INTEGRACIE } from './common.js';

/*
 * Integracie.
 *
 * Zoznam je kratky a meni sa zriedka, preto karty a nie tabulka.
 * Podstatny je stav a jeho dovod - ten je na karte hned pod nazvom.
 */

export function Integrations(): ReactNode {
  const nacitanie = useApi((signal) => integrations.list(signal));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Integrácie"
        detail="Odkiaľ zariadenia prichádzajú a kam ich brána vystavuje."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.integrations.length === 0 ? (
            <EmptyState
              title="Žiadne integrácie"
              detail="Zatiaľ nie je zapnutá žiadna integrácia."
            />
          ) : (
            /*
             * `auto-rows-fr` zrovna vysku vsetkych dlazdic v riadku,
             * `mt-auto` prilepi pätičku na spodok - inak by karta s dlhsim
             * popisom bola vyssia a riadok by vyzeral rozhadzane
             * (ziadost zadavatela 2026-09-16; to iste uz robi zoznam
             * zariadeni).
             */
            <ul className="grid grid-cols-1 auto-rows-fr gap-3 sm:grid-cols-2">
              {data.integrations.map((i) => (
                <Card as="li" key={i.id} className="flex h-full flex-col gap-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="text-(--color-text-subtle)">
                        <IconPlug />
                      </span>
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium text-(--color-text)">{i.name}</span>
                        <span className="truncate font-mono text-xs text-(--color-text-subtle)">
                          {i.id}
                        </span>
                      </div>
                    </div>
                    <Badge tone={TON_INTEGRACIE[i.state]}>{POPIS_INTEGRACIE[i.state]}</Badge>
                  </div>

                  <p className="text-sm text-(--color-text-muted)">{i.detail}</p>

                  {i.loxoneBudget ? <RozpocetLoxone rozpocet={i.loxoneBudget} /> : null}

                  <div className="mt-auto flex flex-wrap items-center gap-2 text-xs text-(--color-text-subtle)">
                    <span>{i.deviceCount} zariadení</span>
                    <span aria-hidden="true">·</span>
                    <span>
                      {[i.isSource ? 'zdroj' : undefined, i.isSink ? 'cieľ' : undefined]
                        .filter(Boolean)
                        .join(' a ')}
                    </span>
                  </div>
                </Card>
              ))}
            </ul>
          )
        }
      </Data>
    </div>
  );
}

/*
 * Rozpocet subscriptions natívneho MQTT klienta Loxone.
 *
 * Miniserver ich zvladne 16 a LoxLink minie na kazde zariadenie dve
 * temy - stav a dostupnost. Cislo je preto na karte MQTT vidno skor,
 * nez sa o tento limit niekto obije v Loxone Config.
 *
 * Limit vsak plati na JEDNEHO klienta. Zadavatel overil v praxi
 * (2026-09-07), ze klientov moze byt viac - kazdy s inou IP adresou
 * brokera. Preto sa nehlasi „nezmesti sa", ale KOLKO KLIENTOV treba.
 */
function RozpocetLoxone({ rozpocet }: { readonly rozpocet: LoxoneRozpocet }): ReactNode {
  const podiel = Math.min(100, Math.round((rozpocet.topics / rozpocet.limit) * 100));
  const ton = rozpocet.fits ? 'bg-(--color-ok)' : 'bg-(--color-warn)';

  return (
    <div className="flex flex-col gap-2 rounded-(--radius-control) border border-(--color-border) p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-(--color-text)">Natívny MQTT klient Loxone</span>
        <span className="font-mono text-xs text-(--color-text-subtle)">
          {rozpocet.topics} / {rozpocet.limit}
        </span>
      </div>

      <div
        role="progressbar"
        aria-label="Využitie subscriptions Miniservera"
        aria-valuemin={0}
        aria-valuemax={rozpocet.limit}
        aria-valuenow={Math.min(rozpocet.topics, rozpocet.limit)}
        className="h-1.5 w-full overflow-hidden rounded-(--radius-pill) bg-(--color-surface-sunken)"
      >
        <div className={`h-full ${ton}`} style={{ width: `${podiel}%` }} />
      </div>

      <p className="text-xs text-(--color-text-muted)">
        {rozpocet.fits
          ? `Jeden MQTT klient Miniservera zvládne ${rozpocet.limit} subscriptions, teda ${rozpocet.maxDevices} zariadení (stav a dostupnosť sú dve témy). Toto sa doň zmestí.`
          : `Jeden klient zvládne ${rozpocet.maxDevices} zariadení, takže na ${rozpocet.deviceCount} ich treba ${rozpocet.clientsNeeded}. Každý musí mať inú IP adresu brokera — brána ich môže mať viac na jednom rozhraní. Alebo použi mapovania do Loxone, ktoré limit nemajú.`}
      </p>
    </div>
  );
}
